import { API_BASE_URL } from "../config/api";

const BALANCE_KEY = "aio_wallet_balance_v1";
const HISTORY_KEY = "aio_wallet_history_v1";

export const REWARD_AMOUNTS = [11, 21, 31, 41, 51, 61, 71, 81, 91, 101];

// NOTE (2026-09-19 security fix): Web wallet ab server-side verified hai.
// - Add money sirf SERVER topup-order flow se hota hai (CreateTopupOrder + UPI
//   payment + admin verification) — client kabhi khud balance credit nahi kar sakta.
// - Balance/history reads + Spend ke liye auth token (X-Auth-Token header) zaroori.

const TOKEN_KEY = "authToken";

const wrap = (mobileNo) => {
  const m = String(mobileNo || "").replace(/\D/g, "").slice(-10);
  return m ? `_${m}` : "";
};

const getStorageKeys = (mobileNo) => ({
  balanceKey: `${BALANCE_KEY}${wrap(mobileNo)}`,
  historyKey: `${HISTORY_KEY}${wrap(mobileNo)}`,
});

const parseNum = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const getMobile = () => {
  try {
    return localStorage.getItem("userMobileNumber") || "";
  } catch { return ""; }
};

const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch { return ""; }
};

// Saare wallet calls Token ke saath (header me). URL me kabhi token nahi.
const authedFetch = async (url, options = {}) => {
  const headers = new Headers(options.headers || {});
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("X-Auth-Token", token);
  return fetch(url, { ...options, headers });
};

// Sync (cache) read — instant display ke liye. Server value syncBalance se aati hai.
export const getBalance = (mobileNo) => {
  const m = mobileNo || getMobile();
  const { balanceKey } = getStorageKeys(m);
  try {
    const raw = localStorage.getItem(balanceKey);
    if (raw !== null) return parseNum(raw);
  } catch {}
  return 0;
};

export const getHistory = (mobileNo) => {
  const m = mobileNo || getMobile();
  const { historyKey } = getStorageKeys(m);
  try {
    const raw = localStorage.getItem(historyKey);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};

const persistHistory = (historyKey, list) => {
  try {
    localStorage.setItem(historyKey, JSON.stringify(list.slice(0, 100)));
  } catch {}
};

// Server se balance lao (auth ke saath) aur cache update karo.
export const syncBalance = async (mobileNo) => {
  const m = mobileNo || getMobile();
  if (!m) return 0;
  try {
    const res = await authedFetch(`${API_BASE_URL}/Wallet/GetBalance?mobileNo=${encodeURIComponent(m)}`);
    const data = await res.json().catch(() => null);
    if (res.ok && data?.success) {
      const bal = parseNum(data.balance);
      const { balanceKey } = getStorageKeys(m);
      localStorage.setItem(balanceKey, String(bal));
      try { window.dispatchEvent(new Event("wallet:updated")); } catch {}
      return bal;
    }
  } catch {}
  return getBalance(m);
};

// Server se history lao (auth) — cache update. Web pe local fake history hatchi aa jayegi.
export const syncHistory = async (mobileNo) => {
  const m = mobileNo || getMobile();
  if (!m) return getHistory(m);
  try {
    const res = await authedFetch(`${API_BASE_URL}/Wallet/GetHistory?mobileNo=${encodeURIComponent(m)}`);
    const data = await res.json().catch(() => null);
    if (res.ok && data?.success) {
      const list = Array.isArray(data.transactions)
        ? data.transactions.map((t) => ({
            id: String(t.Id),
            date: t.CreatedAt,
            note: t.Note || "",
            amount: parseNum(t.Amount),
            type: String(t.Type || "").toLowerCase(),
            balance: parseNum(t.BalanceAfter),
          }))
        : [];
      const { historyKey } = getStorageKeys(m);
      persistHistory(historyKey, list);
      return list;
    }
  } catch {}
  return getHistory(m);
};

// Add Money: SERVER topup order banata hai (client self-credit NOT allowed).
// Return { success, orderId, txnRef, amount } — sirf order; paise abhi WALLET me NAHI aaye.
export const createTopupOrder = async (amount, note) => {
  const m = getMobile();
  const amt = parseNum(amount);
  if (!amt || amt <= 0) return { success: false, reason: "invalid-amount" };
  if (!getToken()) return { success: false, reason: "login-required" };
  try {
    const res = await authedFetch(`${API_BASE_URL}/Wallet/CreateTopupOrder`, {
      method: "POST",
      body: JSON.stringify({ MobileNo: m, Token: getToken(), Amount: amt, Note: note }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) throw new Error(data?.message || "Topup order create failed");
    return { success: true, orderId: data.orderId, txnRef: data.txnRef, amount: parseNum(data.amount) };
  } catch (e) {
    return { success: false, reason: "server-error", message: e.message };
  }
};

// UPI payment ke baad user "Payment Verify" dabata hai. Wallet credit NAHI hota
// yahan — order PAID_PENDING_VERIFICATION hota hai; real credit admin verification baad.
export const confirmTopup = async (orderId, claimed) => {
  const m = getMobile();
  if (!orderId) return { success: false, reason: "invalid-order" };
  try {
    const res = await authedFetch(`${API_BASE_URL}/Wallet/ConfirmTopup`, {
      method: "POST",
      body: JSON.stringify({ MobileNo: m, Token: getToken(), OrderId: orderId, Claimed: !!claimed }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) throw new Error(data?.message || "Confirm failed");
    if (data.balance !== undefined) {
      const { balanceKey } = getStorageKeys(m);
      localStorage.setItem(balanceKey, String(parseNum(data.balance)));
    }
    return { success: true, status: data.status, balance: parseNum(data.balance) };
  } catch (e) {
    return { success: false, reason: "server-error", message: e.message };
  }
};

// Scratch card: reward SERVER decide karta hai. Pick = reserve (no credit).
export const scratchReward = async () => {
  const m = getMobile();
  if (!getToken()) return { success: false, reason: "login-required" };
  try {
    const res = await authedFetch(`${API_BASE_URL}/Wallet/ScratchReward`, {
      method: "POST",
      body: JSON.stringify({ MobileNo: m, Token: getToken() }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) throw new Error(data?.message || "Scratch reward fetch failed");
    return { success: true, reward: parseNum(data.reward), rarity: data.rarity };
  } catch (e) {
    return { success: false, reason: "server-error", message: e.message };
  }
};

// Scratch complete hone ke baad CLAIM — sever-side wallet credit hota hai yahan.
export const scratchRewardClaim = async () => {
  const m = getMobile();
  try {
    const res = await authedFetch(`${API_BASE_URL}/Wallet/ScratchRewardClaim`, {
      method: "POST",
      body: JSON.stringify({ MobileNo: m, Token: getToken() }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) throw new Error(data?.message || "Scratch claim failed");
    const reward = parseNum(data.reward);
    const balance = parseNum(data.balance);
    const { balanceKey, historyKey } = getStorageKeys(m);
    if (data.balance !== undefined) localStorage.setItem(balanceKey, String(balance));
    if (reward > 0) {
      persistHistory(historyKey, [
        { id: `${Date.now()}`, date: new Date().toISOString(), note: `Scratch Card Welcome Reward (${data.rarity || "reward"})`, amount: reward, type: "credit", balance },
        ...getHistory(m),
      ]);
    }
    try { window.dispatchEvent(new Event("wallet:updated")); } catch {}
    return { success: true, reward, rarity: data.rarity, alreadyClaimed: data.alreadyClaimed };
  } catch (e) {
    return { success: false, reason: "server-error", message: e.message };
  }
};

// Debit — server pe balance check hoke minus hota hai (auth-token ke saath).
export const debit = async (amount, note) => {
  const m = getMobile();
  const amt = parseNum(amount);
  if (!amt || amt <= 0) return { success: false, reason: "invalid-amount" };
  if (!getToken()) return { success: false, reason: "login-required" };
  try {
    const res = await authedFetch(`${API_BASE_URL}/Wallet/Spend`, {
      method: "POST",
      body: JSON.stringify({ MobileNo: m, Token: getToken(), Amount: amt, Note: note || "Wallet payment" }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) {
      if (res.status === 400 && /balance/i.test(data?.message || "")) {
        return { success: false, reason: "insufficient-balance", balance: parseNum(data?.balance) };
      }
      throw new Error(data?.message || "Debit failed");
    }
    const balance = parseNum(data.balance);
    const { balanceKey, historyKey } = getStorageKeys(m);
    localStorage.setItem(balanceKey, String(balance));
    persistHistory(historyKey, [
      { id: `${Date.now()}`, date: new Date().toISOString(), note: note || "Wallet payment", amount: amt, type: "debit", balance },
      ...getHistory(m),
    ]);
    try { window.dispatchEvent(new Event("wallet:updated")); } catch {}
    return { success: true, balance };
  } catch (e) {
    return { success: false, reason: "server-error", message: e.message };
  }
};

// Backward compat helper (purana free-credit pattern) — ab KABHI client gift nahi.
// Koi bhi 'credit' web se sirf CreateTopupOrder flow se ho sakta hai.
export const credit = async (amount, note) => {
  const ord = await createTopupOrder(amount, note);
  if (!ord.success) return ord;
  return { success: false, reason: "upi-required", orderId: ord.orderId, txnRef: ord.txnRef, amount: ord.amount, message: "UPI payment ke baad Payment Verify dabayein" };
};

export const UPI_VPA = "9152527030ss-3@okhdfcbank";
export const UPI_PAYEE_NAME = "AIO INDIA";

export const buildUPIUri = ({ amount, note, txnRef }) => {
  const params = {
    pa: UPI_VPA,
    pn: UPI_PAYEE_NAME,
    am: String(amount),
    cu: "INR",
    tn: note || "Wallet Top-up",
  };
  if (txnRef) params.tr = txnRef;
  return (
    "upi://pay?" +
    Object.entries(params)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&")
  );
};

export const makeTxnRef = (mobileNo) => {
  const m = String(mobileNo || "").replace(/\D/g, "").slice(-10);
  return `AIO${m}${Date.now()}`;
};